import React, { useEffect, useState } from 'react';
import userService from '../../services/userService';
import tokenService from '../../services/tokenService';
import { Pencil, Eye, EyeOff, Search, X as LucideX, Copy } from 'lucide-react';

const UserFormModal = ({ open, onClose, user, onUpdated }) => {
  const [form, setForm] = useState({ nombre: '', correo: '', contraseña: '', admin: false, tokenId: '' });
  const [tokens, setTokens] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (user) {
        setForm({
          nombre: user.nombre,
          correo: user.correo,
          contraseña: '',
          admin: user.admin,
          tokenId: user.tokenId || ''
        });
      } else {
        setForm({ nombre: '', correo: '', contraseña: '', admin: false, tokenId: '' });
      }
      setShowPassword(false);
      setError(null);
    }
  }, [user, open]);

  useEffect(() => {
    tokenService.getTokens().then(setTokens).catch(() => setTokens([]));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (user) {
        await userService.updateUser(user.id, form);
      } else {
        await userService.createUser(form);
      }
      onClose();
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl relative flex flex-col max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-2 right-4 text-white bg-red-600 hover:bg-red-800 rounded-full w-10 h-10 flex items-center justify-center text-2xl shadow-lg border-2 border-white"
          title="Cerrar"
        >
          <LucideX size={28} />
        </button>
        <div className="overflow-y-auto p-8 flex-1">
          <h2 className="text-xl font-semibold text-gray-800 mb-5 flex items-center">
            {user ? 'Editar Usuario' : 'Crear Usuario'}
          </h2>
          {error && <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 text-red-700">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Correo</label>
                <input
                  name="correo"
                  type="email"
                  value={form.correo}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Contraseña {user && <span className="text-xs text-gray-500">(dejar vacío para no cambiar)</span>}
              </label>
              <div className="relative">
                <input
                  name="contraseña"
                  type={showPassword ? 'text' : 'password'}
                  value={form.contraseña}
                  onChange={handleChange}
                  required={!user}
                  className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Token asociado</label>
              <select
                name="tokenId"
                value={form.tokenId}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccione un token</option>
                {tokens.map(token => (
                  <option key={token.id} value={token.id}>{token.id} - {token.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                name="admin"
                type="checkbox"
                checked={form.admin}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label className="text-sm font-medium text-gray-700">Administrador</label>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : (user ? 'Actualizar' : 'Crear')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);
  const [success, setSuccess] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getUsers();
      setUsers(data);
    } catch (err) {
      alert('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setEditingUser(null);
    setShowModal(false);
  };

  const handleUserUpdated = async () => {
    // Refrescar la lista de usuarios
    const data = await userService.getUsers();
    setUsers(data);
    // Mostrar mensaje de éxito
    setTimeout(() => setSuccess(editingUser ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente'), 100);
  };


  const handleCopyTokenUsuario = async (user) => {
    try {
      await navigator.clipboard.writeText(user.userToken);
      setCopiedId(user.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Error al copiar token de usuario:', error);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Filtrar usuarios basado en el término de búsqueda
  const filteredUsers = users.filter(user =>
    user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.correo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id.toString().includes(searchTerm) ||
    (user.tokenId && user.tokenId.toString().includes(searchTerm))
  );

  // Calcular paginación
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredUsers.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredUsers.length / recordsPerPage);

  if (loading) return <div className="text-gray-500">Cargando usuarios...</div>;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Gestión de Usuarios</h2>
      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4 text-green-700 transition-opacity duration-500">
          {success}
        </div>
      )}
      <button
        onClick={handleNew}
        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        Crear Usuario
      </button>

      {/* Buscador */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Mostrar</label>
          <select
            value={recordsPerPage}
            onChange={(e) => {
              setRecordsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded px-3 py-1 text-sm pr-8 appearance-none bg-white"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
          <span className="text-sm text-gray-600">registros</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token de usuario</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token ID</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-4">No hay usuarios que coincidan con la búsqueda</td></tr>
            ) : currentRecords.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.nombre}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.correo}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.admin ? 'Sí' : 'No'}</td>
                <td className="px-6 py-4 text-sm text-gray-500" title={user.userToken}><div className="max-w-xs truncate">{user.userToken || '-'}</div></td>
                <td className="px-6 py-4 text-sm text-gray-500">{user.tokenId || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleCopyTokenUsuario(user)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                      title="Copiar token de usuario"
                    >
                      <Copy size={18} />
                      {copiedId === user.id && (
                        <span className="absolute -mt-8 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                          ¡Copiado!
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => handleEdit(user)}
                      className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-50"
                      title="Editar usuario"
                    >
                      <Pencil size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {filteredUsers.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <div>
            Mostrando {currentRecords.length} de {filteredUsers.length} registros
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-blue-500 text-white border rounded disabled:opacity-50"
            >
              Anterior
            </button>
            <span>Página {currentPage} de {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-blue-500 text-white border rounded disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      <UserFormModal
        open={showModal}
        onClose={handleModalClose}
        user={editingUser}
        onUpdated={handleUserUpdated}
      />
    </div>
  );
};

export default UserManagement; 